import { useContext, useEffect, useMemo, useState } from "react";
import AgentContext from "../../../../context/admin/Agent.tsx";
import { useUser } from "../../../../context/UserContext.tsx";
import { Users } from "../../../../models/User.tsx";
import { logUserActivity } from "../../../../services/player/LoginService.tsx";
import {
    createVimplayPlayerBonus,
    getVimplayFreeBetScale,
    getVimplayGameOptions,
    listVimplayPlayerBonuses,
    VimplayFreeBetScale,
    VimplayGameOption,
    VimplayPlayerBonus,
} from "../../../../services/admin/VimplayBonusService.tsx";
import CasinoWeeklyStats from "./CasinoWeeklyStats.tsx";
import "../../../../styles/admin/User.css";
import "../../../../styles/admin/CasinoBonus.css";
import "../../../../styles/admin/CasinoStats.css";

const RESULT_LIMIT = 12;
const SHOW_CASINO_BONUSES = false;

const UserCasinoBonus = () => {
    const { user: currentUser, userIpData } = useUser();
    const { allPlayers } = useContext(AgentContext);
    const [games, setGames] = useState<VimplayGameOption[]>([]);
    const [bonuses, setBonuses] = useState<VimplayPlayerBonus[]>([]);
    const [scale, setScale] = useState<VimplayFreeBetScale | null>(null);
    const [playerQuery, setPlayerQuery] = useState("");
    const [selectedPlayer, setSelectedPlayer] = useState<Users | null>(null);
    const [gameQuery, setGameQuery] = useState("");
    const [gameId, setGameId] = useState("");
    const [freeBetCount, setFreeBetCount] = useState("5");
    const [freeBetAmount, setFreeBetAmount] = useState("");
    const [minRate, setMinRate] = useState("");
    const [expiresOn, setExpiresOn] = useState("");
    const [message, setMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [gamesLoading, setGamesLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setGamesLoading(true);
            try {
                const catalog = await getVimplayGameOptions();
                if (!cancelled) {
                    setGames(catalog.sort((a, b) => a.name.localeCompare(b.name)));
                }
            } catch (error) {
                console.error(error);
                if (!cancelled) {
                    setGames([]);
                    setErrorMessage("Unable to load casino games.");
                }
            } finally {
                if (!cancelled) setGamesLoading(false);
            }
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!selectedPlayer?.id) {
            setBonuses([]);
            return;
        }
        let cancelled = false;
        const loadBonuses = async () => {
            try {
                const rows = await listVimplayPlayerBonuses(selectedPlayer.id!);
                if (!cancelled) setBonuses(rows);
            } catch (error) {
                console.error(error);
                if (!cancelled) setErrorMessage("Unable to load bonuses for this player.");
            }
        };
        void loadBonuses();
        return () => {
            cancelled = true;
        };
    }, [selectedPlayer?.id]);

    useEffect(() => {
        const selected = Number(gameId);
        if (!Number.isInteger(selected) || selected <= 0) {
            setScale(null);
            return;
        }
        let cancelled = false;
        const loadScale = async () => {
            try {
                const next = await getVimplayFreeBetScale(selected);
                if (!cancelled) setScale(next);
            } catch (error) {
                console.error(error);
                if (!cancelled) setScale(null);
            }
        };
        void loadScale();
        return () => {
            cancelled = true;
        };
    }, [gameId]);

    const playerResults = useMemo(() => {
        const term = playerQuery.trim().toLowerCase();
        if (!term || selectedPlayer?.userName.toLowerCase() === term) return [];
        return allPlayers
            .filter((player) => player.userType === "Player" || player.userType == null)
            .filter((player) =>
                player.userName.toLowerCase().includes(term) ||
                (player.name ?? "").toLowerCase().includes(term)
            )
            .slice(0, RESULT_LIMIT);
    }, [allPlayers, playerQuery, selectedPlayer?.userName]);

    const selectedGame = useMemo(
        () => games.find((game) => String(game.id) === gameId) ?? null,
        [games, gameId],
    );
    const crashRateEnabled = selectedGame?.type.toLowerCase() === "crush";

    const gameResults = useMemo(() => {
        const term = gameQuery.trim().toLowerCase();
        if (!term) return [];
        const selected = games.find((game) => String(game.id) === gameId);
        if (selected && selected.name.toLowerCase() === term) return [];
        return games
            .filter((game) =>
                game.name.toLowerCase().includes(term) ||
                String(game.id).includes(term)
            )
            .slice(0, RESULT_LIMIT);
    }, [games, gameQuery, gameId]);

    const selectPlayer = (player: Users) => {
        setSelectedPlayer(player);
        setPlayerQuery(player.userName);
        setMessage("");
        setErrorMessage("");
    };

    const selectGame = (game: VimplayGameOption) => {
        setGameId(String(game.id));
        setGameQuery(game.name);
        if (game.type.toLowerCase() !== "crush") setMinRate("");
        setMessage("");
        setErrorMessage("");
    };

    const saveBonus = async () => {
        if (!selectedPlayer?.id || !gameId || isSaving) return;
        setIsSaving(true);
        setMessage("");
        setErrorMessage("");
        try {
            const expiredAt = expiresOn ? new Date(expiresOn).getTime() : undefined;
            const rate = crashRateEnabled && minRate.trim() ? Number(minRate) : undefined;
            await createVimplayPlayerBonus({
                playerId: selectedPlayer.id,
                gameId: Number(gameId),
                freeBetCount: Number(freeBetCount),
                freeBetAmount: Number(freeBetAmount),
                ...(expiredAt ? { expiredAt } : {}),
                ...(rate != null ? { minRate: rate } : {}),
            });
            if (currentUser?.userName) {
                await logUserActivity(
                    currentUser.userName,
                    `Grant casino bonus ${selectedPlayer.userName}`,
                    userIpData,
                );
            }
            setMessage("Bonus created. It activates when the player opens that game.");
            setFreeBetAmount("");
            const rows = await listVimplayPlayerBonuses(selectedPlayer.id);
            setBonuses(rows);
        } catch (error) {
            const text = error instanceof Error ? error.message : "Unable to create the bonus.";
            setErrorMessage(text);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="container-fluid casino-page">
            <CasinoWeeklyStats />
            {SHOW_CASINO_BONUSES && <div className="container-transaction casino-bonus">
                <div className="casino-bonus-heading">
                    <h1 className="casino-bonus-title">Bonuses</h1>
                    <a
                        className="casino-bonus-guide"
                        href={`${process.env.PUBLIC_URL}/casino-bonus-guide.pdf`}
                        download="casino-bonus-guide.pdf"
                    >
                        Download bonus guide (PDF)
                    </a>
                </div>
                <div className="container-option mt-2">
                    <div className="transaction casino-search">
                        <span className="letters-selected">Player</span>
                        <input
                            className="input-basic"
                            type="search"
                            value={playerQuery}
                            placeholder="Search players"
                            autoComplete="off"
                            aria-label="Search players"
                            onChange={(event) => {
                                setPlayerQuery(event.target.value);
                                setSelectedPlayer(null);
                            }}
                        />
                        {playerQuery.trim() && !selectedPlayer && (
                            <ul className="casino-search-results">
                                {playerResults.length > 0 ? playerResults.map((player) => (
                                    <li key={player.id ?? player.userName}>
                                        <button type="button" onClick={() => selectPlayer(player)}>
                                            <span>{player.userName}</span>
                                            {player.name ? <span className="casino-search-meta">{player.name}</span> : null}
                                        </button>
                                    </li>
                                )) : (
                                    <li className="casino-search-empty">No players match your search.</li>
                                )}
                            </ul>
                        )}
                    </div>
                    <div className="transaction casino-search">
                        <span className="letters-selected">Game</span>
                        <input
                            className="input-basic"
                            type="search"
                            value={gameQuery}
                            placeholder={gamesLoading ? "Loading games..." : "Search games"}
                            autoComplete="off"
                            aria-label="Search games"
                            disabled={gamesLoading}
                            onChange={(event) => {
                                setGameQuery(event.target.value);
                                setGameId("");
                                setMinRate("");
                            }}
                        />
                        {gameQuery.trim() && !gameId && !gamesLoading && (
                            <ul className="casino-search-results">
                                {gameResults.length > 0 ? gameResults.map((game) => (
                                    <li key={game.id}>
                                        <button type="button" onClick={() => selectGame(game)}>
                                            <span>{game.name}</span>
                                        </button>
                                    </li>
                                )) : (
                                    <li className="casino-search-empty">No games match your search.</li>
                                )}
                            </ul>
                        )}
                    </div>
                </div>
                <div className="container-option mt-2">
                    <div className="transaction">
                        <span className="letters-selected">Free bets</span>
                        <input
                            className="input-basic"
                            type="number"
                            min="1"
                            step="1"
                            value={freeBetCount}
                            onChange={(event) => setFreeBetCount(event.target.value)}
                        />
                    </div>
                    <div className="transaction">
                        <span className="letters-selected">Amount each</span>
                        <input
                            className="input-basic"
                            type="number"
                            min={scale?.minBet}
                            max={scale?.maxBet}
                            step={scale?.step || "0.001"}
                            value={freeBetAmount}
                            onChange={(event) => setFreeBetAmount(event.target.value)}
                        />
                    </div>
                </div>
                {scale && (
                    <p className="mt-2 casino-bonus-note">
                        Scale: min {scale.minBet}, max {scale.maxBet}, step {scale.step}
                        {scale.buttons?.length ? `. Buttons: ${scale.buttons.join(", ")}` : ""}
                    </p>
                )}
                <div className="container-option mt-2">
                    <div className="transaction">
                        <span className="letters-selected">Crash min rate</span>
                        <input
                            className="input-basic casino-bonus-rate"
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder={crashRateEnabled ? "Optional" : "Crash games only"}
                            value={crashRateEnabled ? minRate : ""}
                            disabled={!crashRateEnabled}
                            onChange={(event) => setMinRate(event.target.value)}
                        />
                    </div>
                    <div className="transaction">
                        <span className="letters-selected">Expires</span>
                        <input
                            className="input-basic"
                            type="datetime-local"
                            value={expiresOn}
                            onChange={(event) => setExpiresOn(event.target.value)}
                        />
                    </div>
                </div>
                <p className="mt-2 casino-bonus-note">
                    Each free bet uses that amount. The stake does not touch available or free play.
                    A win is paid to available. The bonus turns on when the player opens the game.
                </p>
                {message && <p className="mt-2 casino-bonus-note">{message}</p>}
                {errorMessage && <p className="mt-2 text-danger">{errorMessage}</p>}
                <button
                    className="mt-2 button-user button-color-freeplay"
                    type="button"
                    disabled={isSaving || !selectedPlayer?.id || !gameId}
                    onClick={() => void saveBonus()}
                >
                    {isSaving ? "Saving..." : "Create bonus"}
                </button>

                <div className="table-responsive freeplay">
                    <table className="mt-3 table table-freeplay">
                        <thead className="header-customer">
                            <tr>
                                <th>Game</th>
                                <th>Amount</th>
                                <th>Left</th>
                                <th>Status</th>
                                <th>Code</th>
                            </tr>
                        </thead>
                        <tbody className="body-customer">
                            {selectedPlayer && bonuses.length === 0 && (
                                <tr>
                                    <td colSpan={5}>This player has no casino bonuses.</td>
                                </tr>
                            )}
                            {bonuses.map((bonus) => (
                                <tr key={bonus.uniqueCode}>
                                    <td>{bonus.gameName || bonus.gameId}</td>
                                    <td>${bonus.freeBetAmount}</td>
                                    <td>{bonus.freeBetRemaining} / {bonus.freeBetCount}</td>
                                    <td>{bonus.status}</td>
                                    <td>{bonus.uniqueCode}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>}
        </div>
    );
};

export default UserCasinoBonus;
